import { PlayerEvents, AdActions, type ActivePodcastOrStation } from '@4tn/core-audio-player-shared';
import Cookies from 'js-cookie';
import { config as jwPlayerConfig, getAdTag } from './utils';
import EventDispatcher from '@utils/event-dispatcher';
import { PREROLL_ID } from './consts';
import { type IConfig } from '@players/types';
import logger from '@utils/logger';

export const handleVastPlay = async (
  media: ActivePodcastOrStation,
  config: IConfig,
  audioPlayerCallback: () => Promise<void>
): Promise<void> => {
  const tag = getAdTag(config, media);
  const jwPlayerSetup = jwPlayerConfig();
  const jwPlayer = window.jwplayer(PREROLL_ID).setup(jwPlayerSetup);

  jwPlayer.on('ready', () => {
    try {
      localStorage.setItem('jwplayer.mute', 'false');
    } catch (error) {
      logger.error("Failed to set 'jwplayer.mute' in localStorage");
    }
    jwPlayer?.playAd(tag);
  });

  jwPlayer.on('adBreakStart', () => {
    EventDispatcher.sendEvent(AdActions.AD_START);
  });

  jwPlayer.on('adComplete', () => {
    jwPlayer?.remove();

    // Set the cookie after the ad is finished
    Cookies.set('lastAdPlayedTime', Date.now().toString(), { expires: 30 * 60 });

    audioPlayerCallback()
      .then(() => EventDispatcher.sendEvent(AdActions.AD_STOP))
      .catch(() => EventDispatcher.sendEvent(AdActions.AD_ERRORED));
  });

  jwPlayer.on('adError', () => {
    jwPlayer?.remove();

    audioPlayerCallback()
      .then(() => EventDispatcher.sendEvent(AdActions.AD_STOP))
      .catch(() => EventDispatcher.sendEvent(AdActions.AD_ERRORED));
  });

  jwPlayer.on('adBlock', () => {
    jwPlayer?.remove();

    audioPlayerCallback()
      .then(() => EventDispatcher.sendEvent(AdActions.AD_BLOCKER_DETECTED))
      .catch(() => EventDispatcher.sendEvent(AdActions.AD_ERRORED));
  });

  jwPlayer.on('playAttempt', () => {
    jwPlayer?.remove();

    EventDispatcher.sendEvent(AdActions.AD_STOP);
  });

  // change opacity when we know we have ad impression, if doesn't jwplayer will be removed
  jwPlayer.on('adImpression', () => {
    const prerollDiv = document.getElementById('preroll');
    const overlay = document.getElementById('overlay');
    if (!prerollDiv) {
      return;
    }

    prerollDiv.style.opacity = '1';

    if (overlay) {
      overlay.style.opacity = '0.8';
    }
  });

  jwPlayer.on('stop', () => {
    jwPlayer?.remove();

    EventDispatcher.sendEvent(AdActions.AD_STOP);
  });

  jwPlayer.on('error', () => {
    jwPlayer?.remove();

    audioPlayerCallback()
      .then(() => EventDispatcher.sendEvent(AdActions.AD_STOP))
      .catch(() => EventDispatcher.sendEvent(AdActions.AD_ERRORED));
  });

  jwPlayer.on('setupError', () => {
    jwPlayer?.remove();

    audioPlayerCallback()
      .then(() => EventDispatcher.sendEvent(AdActions.AD_STOP))
      .catch(() => EventDispatcher.sendEvent(AdActions.AD_ERRORED));
  });
};

EventDispatcher.on(PlayerEvents.STREAM_ERROR, () => {
  const overlay = document.getElementById('overlay');
  if (overlay) {
    overlay.remove();
  }
});
